import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing'

const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

function jitterSuffix(len = 3): string {
  const bytes = new Uint8Array(len)
  crypto.getRandomValues(bytes)
  let s = ''
  for (let i = 0; i < len; i++) {
    // Last digit must not be '0' (fractional-indexing rejects trailing zeros).
    const d = i === len - 1 ? 1 + (bytes[i]! % 61) : bytes[i]! % 62
    s += DIGITS[d]
  }
  return s
}

/**
 * A fractional-index key strictly between `a` and `b` (either may be null = open end),
 * with random jitter so two clients inserting at the same spot concurrently get
 * distinct keys (SPEC §4.2). Siblings are always sorted by (order, id), so exact ties
 * are still deterministic.
 */
export function orderBetween(a: string | null | undefined, b: string | null | undefined): string {
  const lo = a ?? null
  let hi = b ?? null
  if (lo !== null && hi !== null && lo >= hi) {
    // Concurrent edits produced equal/inverted neighbours. Place just after `lo`.
    hi = null
  }
  let key: string
  try {
    key = generateKeyBetween(lo, hi)
  } catch {
    key = generateKeyBetween(lo, null)
  }
  const jittered = key + jitterSuffix()
  if (hi === null || jittered < hi) return jittered
  return key
}

/** `n` ordered keys between a and b (for bulk inserts, e.g. multi-line paste). */
export function ordersBetween(a: string | null | undefined, b: string | null | undefined, n: number): string[] {
  if (n <= 0) return []
  let lo = a ?? null
  let hi = b ?? null
  if (lo !== null && hi !== null && lo >= hi) hi = null
  try {
    return generateNKeysBetween(lo, hi, n)
  } catch {
    const out: string[] = []
    for (let i = 0; i < n; i++) {
      lo = orderBetween(lo, hi)
      out.push(lo)
    }
    return out
  }
}

export function compareOrder(a: { order: string; id: string }, b: { order: string; id: string }): number {
  if (a.order < b.order) return -1
  if (a.order > b.order) return 1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/** Sort key for Day cards: dayOrder, falling back to order. */
export function compareDayOrder(
  a: { dayOrder?: string; order: string; id: string },
  b: { dayOrder?: string; order: string; id: string },
): number {
  const ka = a.dayOrder ?? '~' + a.order
  const kb = b.dayOrder ?? '~' + b.order
  if (ka < kb) return -1
  if (ka > kb) return 1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}
